import { animated } from '@react-spring/web';
import { Box, Button, Flex, parseColor, Stack, Table, Td, Th, Z_INDEX } from '@talos/kyoko';
import styled from 'styled-components';

const MOBILE_SHEET_TOP_RADIUS = 32;
const MOBILE_STICKY_TABS_Z_INDEX = Z_INDEX.blotters;
const MOBILE_STICKY_STACK_Z_INDEX = MOBILE_STICKY_TABS_Z_INDEX + 1;
export const MOBILE_STICKY_CONTROL_Z_INDEX = MOBILE_STICKY_STACK_Z_INDEX + 1;
const MOBILE_STICKY_TABS_HEIGHT_MULTIPLIER = 2.5;

export const OrderAnalyticsWrapper = styled(animated(Box))<{ $isMobile?: boolean }>`
  // Because of the animated(...) usage, types become very wide. Need to do type narrowing on background below to keep ts happy.
  background: ${({ background, theme }) =>
    (typeof background === 'string' ? parseColor(theme, background) : undefined) ?? theme.colors.gray['020']};
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: ${({ $isMobile }) => ($isMobile ? 'hidden' : 'visible')};
  border-top-left-radius: ${({ $isMobile }) => ($isMobile ? `${MOBILE_SHEET_TOP_RADIUS}px` : 0)};
  border-top-right-radius: ${({ $isMobile }) => ($isMobile ? `${MOBILE_SHEET_TOP_RADIUS}px` : 0)};
`;

export const Container = styled(Stack)`
  min-height: 0;
  overflow-y: auto;
  overflow-y: overlay;
  overflow-x: hidden;
  flex: 1 1 0;
  position: relative;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;

  & > :last-child {
    margin-left: ${({ theme }) => theme.spacing4}px;
  }
`;

export const Summary = styled.div`
  background: ${({ theme }) => theme.backgroundDrawer};
  padding: ${({ theme }) => `${theme.spacing16}px ${theme.spacing16}px`};
  border-bottom: ${({ theme }) => `1px solid ${theme.backgroundBody}`};
`;

export const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing16}px;

  > div {
    justify-content: center;
    flex-direction: column;
    display: flex;
    flex: 1;
  }

  > div:first-child(1) {
    align-items: flex-start;
  }
  > div:nth-child(2) {
    align-items: center;
  }
  > div:last-child {
    align-items: flex-end;
  }
`;

export const Meta = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing16}px;

  > div {
    display: flex;
    align-items: center;
  }
`;

export const MobileHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing8}px;
  padding: ${({ theme }) => theme.spacing8}px ${({ theme }) => theme.spacing16}px;
  padding-top: calc(${({ theme }) => theme.spacing24}px + 2px);
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray['000']};
  flex: 0 0 auto;
`;

export const MobileStickyStack = styled.div`
  position: relative;
  z-index: ${MOBILE_STICKY_STACK_Z_INDEX};
  background: ${({ theme }) => theme.colors.gray.main};
  flex: 0 0 auto;
`;

export const MobileStickyTabs = styled.div<{ $top: number }>`
  position: sticky;
  top: ${({ $top }) => $top}px;
  z-index: ${MOBILE_STICKY_TABS_Z_INDEX};
  background: ${({ theme }) => theme.colors.gray.main};
  border-top: 1px solid ${({ theme }) => theme.backgroundBody};
`;

export const IndicatorBadgeStack = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${({ theme }) => theme.spacing8}px;
`;

export const TitleRight = styled.div`
  text-align: right;
  span {
    margin-left: auto;
    color: ${({ theme }) => theme.colors.gray['070']};
    font-weight: ${({ theme }) => theme.fontWeightMedium};
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: ${({ theme }) => theme.fontSizeDefault}rem;
  }
`;

export const OrderAnalyticsTabContent = styled.div<{ isMobile?: boolean }>`
  flex: 1;
  min-height: 0;
  background: ${({ theme }) => theme.backgroundDrawer};
  padding: ${({ isMobile, theme }) => (isMobile ? `${theme.spacing16}px` : `${theme.spacing16}px ${theme.spacing8}px`)};
  overflow: ${({ isMobile }) => (isMobile ? 'visible' : 'auto')};
  scrollbar-gutter: ${({ isMobile }) => (isMobile ? 'auto' : 'stable both-edges')};
`;

export const AnalyticsTabWrapper = styled(OrderAnalyticsTabContent)`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing8}px 0;
`;

export const TradesTabWrapper = styled.div`
  flex: 1;
  min-height: 0;
`;

export const TradesTabContent = styled.div`
  flex: 1;
  min-height: 0;
  background: ${({ theme }) => theme.backgroundDrawer};

  [data-testid='blotter-table-wrapper'] {
    height: auto;
  }

  .ag-root-wrapper,
  .ag-root-wrapper-body,
  .ag-root,
  .ag-body,
  .ag-body-viewport,
  .ag-center-cols-viewport {
    overflow: visible !important;
  }

  .ag-header {
    position: -webkit-sticky !important;
    position: sticky !important;
    top: ${({ theme }) => theme.baseSize * MOBILE_STICKY_TABS_HEIGHT_MULTIPLIER}px !important;
    z-index: ${MOBILE_STICKY_TABS_Z_INDEX - 1};
    background: var(--ag-header-background-color);
  }
`;

export const Footer = styled.div`
  display: flex;
  flex: 0;
  align-items: flex-end;
  padding: ${({ theme }) => theme.spacing16}px;
  gap: ${({ theme }) => theme.spacing8}px;

  ${Button} {
    flex: 1;
  }
`;

export const Description = styled.div<{ isMobile?: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  grid-column-gap: ${({ theme }) => theme.spacing8}px;
  grid-row-gap: ${({ isMobile, theme }) => (isMobile ? `${theme.spacing8}px` : '0')};
  grid-auto-rows: 1fr;
`;

export const Tables = styled(Flex)`
  &:first-child {
    margin-top: 0;
  }

  > ${Table} {
    flex-grow: 1;
    width: 50%;
  }

  > ${Table}:only-child {
    width: 100%;
  }

  & + & {
    border-top: ${({ theme }) => `1px solid ${theme.colors.gray['040']}`};
  }

  ${Th}, ${Td} {
    padding-left: 0;
    padding-right: 0;
  }
`;
Tables.defaultProps = {
  alignItems: 'flex-start',
  gap: 'spacing16',
};

export const TradeMarkerPopover = styled.div`
  width: auto;
  height: auto;
  position: absolute;
  display: none;
  padding: ${({ theme }) => theme.spacing4}px;
  box-sizing: border-box;
  font-size: ${({ theme }) => theme.fontSizeDefault}rem;
  color: ${({ theme }) => theme.colors.gray['100']};
  background: ${({ theme }) => theme.colors.gray.main};
  text-align: left;
  z-index: 100;
  pointer-events: none;
`;

export const ChartMeta = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  color: ${({ theme }) => theme.colors.gray['070']};
  display: flex;
  align-items: center;
  z-index: 100;

  > * + * {
    margin-left: ${({ theme }) => theme.spacing4}px;
  }
`;

export const MetricLabel = styled.span`
  ${({ theme }) => `border-bottom: dotted 1px ${theme.colors.gray['080']}`};
`;
